import { AxiosError, isAxiosError } from 'axios';

interface IUpdateOfficeApiResponseData {
  message?: string;
  error?: string;
  data?: { message?: string; error?: string };
}

const firstString = (...values: Array<string | undefined>): string | undefined =>
  values.find((v): v is string => typeof v === 'string' && v.trim().length > 0);

/** Names the form field that caused a server-side rejection, so the form
 *  can paint its error border without needing to pattern-match BE copy. */
export type TUpdateOfficeErrorField =
  | 'officeName'
  | 'officeAddress'
  | 'officeType'
  | 'officeSpecialty';

/**
 * Catch-all for axios errors that carry a BE-supplied message. Placed last in
 * the service chain so the user sees whatever copy the BE wrote instead of the
 * generic axios "Request failed with status code …" string.
 */
export class UpdateOfficeApiError extends Error {
  readonly field?: TUpdateOfficeErrorField;

  constructor(message: string, field?: TUpdateOfficeErrorField) {
    super(message);
    this.name = 'UpdateOfficeApiError';
    this.field = field;
  }

  static fromAxiosError(
    error: unknown,
    field?: TUpdateOfficeErrorField,
  ): UpdateOfficeApiError | null {
    if (!isAxiosError(error)) {
      return null;
    }

    const axiosError = error as AxiosError<IUpdateOfficeApiResponseData>;
    const data = axiosError.response?.data;
    const message = firstString(
      data?.message,
      data?.error,
      data?.data?.message,
      data?.data?.error,
    );

    return message ? new UpdateOfficeApiError(message, field) : null;
  }
}
